import { site } from '../../content/site'
import { applyAlias, shellAliases } from './aliases'
import { commandCount, find, manLines, visibleNames } from './commands'
import { filterNames, filters } from './filters'
import { blank, line, span, text } from './format'
import { HOME, completePath } from './fs'
import { t } from './messages'
import { machineLines } from './render'
import type { Exec, Line, ShellState, Span } from './types'

export { shellAliases }

type Token = { value: string; quoted: boolean }

/** Splits on whitespace while honouring single and double quotes. */
export function tokenize(input: string): Token[] {
  const tokens: Token[] = []
  let value = ''
  let quote: '"' | "'" | undefined
  let started = false
  let singleQuoted = false

  for (const character of input) {
    if (quote) {
      if (character === quote) quote = undefined
      else value += character
      continue
    }
    if (character === '"' || character === "'") {
      quote = character
      started = true
      if (character === "'") singleQuoted = true
      continue
    }
    if (/\s/.test(character)) {
      if (started || value) tokens.push({ value, quoted: singleQuoted })
      value = ''
      started = false
      singleQuoted = false
      continue
    }
    value += character
    started = true
  }
  if (started || value) tokens.push({ value, quoted: singleQuoted })
  return tokens
}

/** `$?`, `$VAR`, `${VAR}` and a leading `~`. Single-quoted words are literal. */
export function expand(token: Token, state: ShellState): string {
  if (token.quoted) return token.value
  const variables: Record<string, string> = {
    ...state.env,
    PWD: state.cwd,
    '?': String(state.status),
  }
  const substituted = token.value.replace(/\$\{(\w+)\}|\$(\?|\w+)/g, (match, braced, bare) => {
    const name = braced ?? bare
    return name in variables ? variables[name] : match
  })
  if (substituted === '~') return HOME
  return substituted.startsWith('~/') ? `${HOME}${substituted.slice(1)}` : substituted
}

/** Splits a command line on top-level `|`, ignoring pipes inside quotes. */
export function splitPipeline(input: string): string[] {
  const segments: string[] = []
  let current = ''
  let quote: string | undefined
  for (const character of input) {
    if (quote) {
      if (character === quote) quote = undefined
      current += character
      continue
    }
    if (character === '"' || character === "'") {
      quote = character
      current += character
      continue
    }
    if (character === '|') {
      segments.push(current)
      current = ''
      continue
    }
    current += character
  }
  segments.push(current)
  return segments.map((segment) => segment.trim())
}

/** `!!` replays the previous command, the one history expansion worth having. */
export const expandHistory = (input: string, history: string[]): string =>
  input.trim() === '!!' ? history.at(-1) ?? input : input

/**
 * Splits a line into `[operator, command]` pairs on top-level `;`, `&&` and
 * `||`. Runs before the pipe splitter, so `a || b` is not mistaken for a pipe.
 */
export function splitSequence(input: string): { operator: ';' | '&&' | '||'; command: string }[] {
  const parts: { operator: ';' | '&&' | '||'; command: string }[] = []
  let current = ''
  let operator: ';' | '&&' | '||' = ';'
  let quote: string | undefined

  for (let index = 0; index < input.length; index += 1) {
    const character = input[index]
    if (quote) {
      if (character === quote) quote = undefined
      current += character
      continue
    }
    if (character === '"' || character === "'") {
      quote = character
      current += character
      continue
    }
    const pair = input.slice(index, index + 2)
    if (pair === '&&' || pair === '||') {
      parts.push({ operator, command: current.trim() })
      operator = pair
      current = ''
      index += 1
      continue
    }
    if (character === ';') {
      parts.push({ operator, command: current.trim() })
      operator = ';'
      current = ''
      continue
    }
    current += character
  }
  parts.push({ operator, command: current.trim() })
  return parts.filter((part, index) => part.command || index === 0)
}

/** Runs one `;`/`&&`/`||` list, short-circuiting on the exit status. */
export function interpret(input: string, state: ShellState): Exec {
  const sequence = splitSequence(expandHistory(input, state.history))
  if (sequence.length === 1) return runPipeline(sequence[0].command, state)

  const lines: Line[] = []
  const side: Omit<Partial<Exec>, 'lines' | 'status' | 'stream'> = {}
  let status = state.status
  let current = state

  for (const part of sequence) {
    if (part.operator === '&&' && status !== 0) continue
    if (part.operator === '||' && status === 0) continue

    const result = runPipeline(part.command, current)
    status = result.status
    if (result.clear) {
      lines.length = 0
      side.clear = true
    }
    lines.push(...result.lines)
    // `cd projects && ls` must see the new directory in the second command.
    if (result.cwd) {
      current = { ...current, cwd: result.cwd }
      side.cwd = result.cwd
    }
    if (result.locale) {
      current = { ...current, locale: result.locale }
      side.locale = result.locale
    }
    if (result.open) side.open = result.open
    if (result.theme) side.theme = result.theme
    if (result.historyClear) side.historyClear = true
    if (result.title) side.title = result.title
    if (result.gui) side.gui = result.gui
  }
  return { ...side, lines, status }
}

function runPipeline(input: string, state: ShellState): Exec {
  const segments = splitPipeline(input)
  const head = segments[0] ?? ''
  if (!head) return { lines: [], status: state.status }

  const tokens = tokenize(applyAlias(head))
  const name = tokens[0]?.value
  if (!name) return { lines: [], status: state.status }

  const command = find(name)
  if (!command) {
    return {
      lines: [
        line(span('zsh: command not found: ', 'err'), span(name)),
        text(`did you mean: ${suggest(name).join(', ') || 'help'}?`, 'dim'),
      ],
      status: 127,
    }
  }

  const args = tokens.slice(1).map((token) => expand(token, state))
  const result = args.includes('--help')
    ? { lines: manLines(command, state), status: 0 }
    : command.run(args, state)

  if (segments.length === 1) return result

  /*
   * Every stage of a pipeline runs in a subshell, so side effects are dropped:
   * `cd .. | wc -l` must not move the shell, and `open link-1 | wc -l` must not
   * spawn a tab. Only the text stream and the final exit status survive.
   */
  let lines = result.lines
  let status = result.status
  for (const segment of segments.slice(1)) {
    const stage = tokenize(segment)
    const filter = stage[0] ? filters[stage[0].value] : undefined
    if (!filter) {
      return {
        lines: [text(`zsh: command not found: ${stage[0]?.value ?? ''}`, 'err')],
        status: 127,
      }
    }
    const applied = filter.apply(
      lines,
      stage.slice(1).map((token) => expand(token, state)),
    )
    lines = applied.lines
    status = applied.status
  }

  return { lines, status }
}

/** Cheap edit-distance-ish suggestion for the "command not found" hint. */
function suggest(name: string): string[] {
  const lower = name.toLowerCase()
  return visibleNames()
    .filter(
      (candidate) =>
        candidate.startsWith(lower.slice(0, 2)) ||
        candidate.includes(lower) ||
        lower.includes(candidate),
    )
    .slice(0, 3)
}

/* ------------------------------------------------------------ completion */

export type Completion = {
  /** Full replacement words for the token under the cursor. */
  candidates: string[]
  /** Index in the input where that token starts. */
  start: number
}

export const longestCommonPrefix = (values: string[]): string => {
  if (!values.length) return ''
  return values.reduce((prefix, value) => {
    let index = 0
    while (index < prefix.length && prefix[index] === value[index]) index += 1
    return prefix.slice(0, index)
  })
}

export function complete(input: string, state: ShellState): Completion {
  const prefix = /(\S*)$/.exec(input)?.[1] ?? ''
  const start = input.length - prefix.length
  const before = input.slice(0, start)
  const pipeIndex = before.lastIndexOf('|')
  const segment = before.slice(pipeIndex + 1).trim()
  const words = segment ? segment.split(/\s+/) : []

  // First word of a pipeline stage: filters after `|`, commands otherwise.
  if (!words.length) {
    const pool = pipeIndex >= 0 ? filterNames : [...visibleNames(), ...Object.keys(shellAliases)]
    return { candidates: unique(pool.filter((value) => value.startsWith(prefix))), start }
  }
  if (pipeIndex >= 0) return { candidates: [], start }

  const command = find(shellAliases[words[0]]?.split(' ')[0] ?? words[0])
  const candidates = command?.complete
    ? command.complete(prefix, state)
    : completePath(prefix, state)
  return { candidates: unique(candidates), start }
}

const unique = (values: string[]): string[] => [...new Set(values)].sort()

/* ---------------------------------------------------------------- prompt */

/**
 * oh-my-zsh `robbyrussell`: a green arrow that turns red after a failure, the
 * basename of the working directory, and — only inside the repository — the
 * git segment. A `git:(main)` in `/etc` is the giveaway of a faked prompt.
 */
export function promptSpans({ cwd, status }: Pick<ShellState, 'cwd' | 'status'>): Span[] {
  const folder = cwd === HOME ? '~' : cwd.split('/').filter(Boolean).at(-1) ?? '/'
  const inRepository = cwd === HOME || cwd.startsWith(`${HOME}/`)
  return [
    { text: '➜', style: status === 0 ? 'ok' : 'err' },
    { text: '  ' },
    { text: folder, style: 'accent' },
    ...(inRepository
      ? ([
          { text: ' git:(', style: 'git' },
          { text: 'main', style: 'err' },
          { text: ')', style: 'git' },
        ] as Span[])
      : []),
  ]
}

export const promptText = (state: Pick<ShellState, 'cwd' | 'status'>): string =>
  promptSpans(state)
    .map((item) => item.text)
    .join('')

/* ------------------------------------------------------------------ boot */

export function boot(state: ShellState): Line[] {
  const ready = t('bootReady', state.locale).split('{n}')
  return [
    text(t('bootLast', state.locale), 'dim'),
    blank(),
    ...machineLines('cadillac'),
    blank(),
    line(
      span(t('bootNow', state.locale), 'dim'),
      span(site.work.company, 'accent'),
      span(` — ${site.work.role[state.locale]} `),
      span(t('bootNowHint', state.locale), 'dim'),
    ),
    line(
      span(ready[0], 'dim'),
      span(String(commandCount), 'num'),
      span(ready[1] ?? '', 'dim'),
      span(' '),
      span(t('bootHint', state.locale), 'dim'),
    ),
  ]
}
